import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload, ApiResponse } from '@ocean/api'
import { ToastUtils, LocalizationUtils as OceanLocalizationUtils } from '@ocean/utils'
import { AppThunk } from 'store'
import { MiddlewareApiAction, createApiAction } from 'api'
import { ReferrerUtils, LocalizationUtils } from 'utils'
import { userLoggedOut } from 'core/session/slices/session'
import {
  convertFromScormTimeToSeconds,
  convertToScormTimeSpan,
} from 'scorm/helpers/scormRuntimeHelper'
import {
  ScormDisplayData,
  ScormData,
  WritableScormRuntimeData,
  CoreLessonStatus,
  CoreExit,
} from 'scorm/types'
import {
  getElearningDetails,
  createElearningResult,
  startElearningSession,
  updateElearningProgress,
  finalizeElearningResult,
  deleteElearningResult,
  getFeedbackForm,
  saveFeedback,
} from 'modules/activityDetails/ElearningDetails/api/elearningDetailsService'
import ElearningScormDataBuilder from 'modules/activityDetails/ElearningDetails/slices/helpers/elearningScormDataBuilder'
import {
  ElearningProgressUpdateDto,
  ApiStartElearningSessionResponse,
  FeedbackAnswer,
  FeedbackFreeTextAnswer,
  CreateResultResponse,
  ElearningRetractionCommand,
} from 'modules/activityDetails/ElearningDetails/types'
import {
  ElearningDetailsState,
  ApiElearningDetailsResponse,
  ApiFeedbackFormResponse,
  ApiSaveFeedbackResponse,
} from './types'

const initialState: ElearningDetailsState = {
  elearningDetailsData: {},
  runningChapterScromData: null,
  runningChapterId: null,
  activeElearningSessionId: null,
  elearningFeedbackFormData: {},
  saveFeedbackData: undefined,
  isFeedbackVisible: false,
  activeResultId: null,
  activeLanguage: '',
  commitsInProgress: 0,
  finishScheduled: false,
  scheduledFinishData: null,
  spinnerVisible: false,
  isRetractionInfoVisible: false,
}

const elearningDetailsSlice = createSlice({
  name: 'elearningDetails',
  initialState,
  reducers: {
    elearningDetailsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiElearningDetailsResponse>>
    ) {
      state.elearningDetailsData = action.payload

      const elearningDetails = action.payload.result?.elearningDetails
      if (elearningDetails) {
        state.activeResultId = elearningDetails.result?.id || null

        const isResultCompleted = elearningDetails.result?.isCompleted
        const language = isResultCompleted
          ? elearningDetails.listOfLanguages[0] || 'EN'
          : elearningDetails.result?.languageKey || 'EN'
        state.activeLanguage = language
        state.isRetractionInfoVisible = true
      }
    },
    clearElearningDetails(state) {
      state.elearningDetailsData = initialState.elearningDetailsData
    },
    setChapterToRunId(state, action) {
      state.runningChapterId = action.payload
    },
    resultCreated(state, action) {
      state.activeResultId = action.payload
    },
    elearninSessionReady(state, action) {
      state.activeElearningSessionId = action.payload
    },
    scormDataCreated(state, action: PayloadAction<ScormData>) {
      state.runningChapterScromData = action.payload
    },
    closeChapter(state) {
      state.runningChapterScromData = null
      state.runningChapterId = null
      state.activeElearningSessionId = null
      state.activeResultId = null
      state.commitsInProgress = 0
      state.finishScheduled = false
      state.scheduledFinishData = null
    },
    increaseCommitsInProgress(state) {
      state.commitsInProgress += 1
    },
    decreaseCommitsInProgress(state) {
      if (state.commitsInProgress > 0) {
        state.commitsInProgress -= 1
      }
    },
    setFinishScheduled(state, action) {
      state.finishScheduled = action.payload
    },
    setScheduledFinishData(state, action) {
      state.scheduledFinishData = action.payload
    },
    feedbackFormLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiFeedbackFormResponse>>
    ) {
      state.elearningFeedbackFormData = action.payload
    },
    elearningFeedbackSaved(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiSaveFeedbackResponse>>
    ) {
      state.saveFeedbackData = action.payload
    },
    clearElearningFeedbackSaved(state) {
      state.saveFeedbackData = undefined
    },
    setFeedbackVisible(state, action: PayloadAction<boolean>) {
      state.isFeedbackVisible = action.payload
    },
    showSpinner(state) {
      state.spinnerVisible = true
    },
    hideSpinner(state) {
      state.spinnerVisible = false
    },
    setActiveLanguage(state, action) {
      state.activeLanguage = action.payload
    },
    resumeTraining(state) {
      state.isRetractionInfoVisible = false
    },
    noop() {},
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const {
  noop,
  elearningDetailsLoaded,
  clearElearningDetails,
  resultCreated,
  elearninSessionReady,
  closeChapter,
  increaseCommitsInProgress,
  scormDataCreated,
  feedbackFormLoaded,
  elearningFeedbackSaved,
  clearElearningFeedbackSaved,
  decreaseCommitsInProgress,
  setFinishScheduled,
  setScheduledFinishData,
  showSpinner,
  hideSpinner,
  setFeedbackVisible,
  resumeTraining,
} = elearningDetailsSlice.actions

export { clearElearningFeedbackSaved, setFeedbackVisible, clearElearningDetails }
export default elearningDetailsSlice.reducer

export const setRunningChapterId = elearningDetailsSlice.actions.setChapterToRunId

export const setActiveLanguage = elearningDetailsSlice.actions.setActiveLanguage

export const loadElearningDetailsData = (elearningId: Guid, competenceId?: Guid) => {
  return createApiAction({
    apiCalls: {
      elearningDetails: () => getElearningDetails(elearningId, competenceId),
    },
    payloadAction: elearningDetailsLoaded,
    getResultToKeep: state => state.elearningDetails.elearningDetailsData.result,
  })
}

export const createResult =
  (moduleId: Guid, startChapter: boolean = false): AppThunk =>
  async (dispatch, getState) => {
    dispatch(showSpinner())

    const { activeLanguage } = getState().elearningDetails
    const response = await createElearningResult(moduleId, activeLanguage)

    dispatch(hideSpinner())
    const { error, content }: ApiResponse<CreateResultResponse> = response
    if (error || !content) {
      ToastUtils.error(LocalizationUtils.localizeError(error?.code))
      return
    }
    dispatch(resultCreated(content.resultId))
    if (startChapter) dispatch(runChapter())
  }

export const runChapter = (): AppThunk => async (dispatch, getState) => {
  const { runningChapterId, activeResultId } = getState().elearningDetails
  if (!runningChapterId || !activeResultId) return
  dispatch(startSession(runningChapterId, activeResultId) as MiddlewareApiAction)
}

const handleScormData =
  (response: ApiMiddlewarePayload<ApiStartElearningSessionResponse>): AppThunk =>
  async (dispatch, getState) => {
    dispatch(hideSpinner())
    dispatch(setFinishScheduled(false))

    const { error, result } = response
    if (error || !result) {
      ToastUtils.error(LocalizationUtils.localizeError(error?.code))
      return
    }

    const elearningDetails =
      getState().elearningDetails.elearningDetailsData.result?.elearningDetails
    if (!elearningDetails) {
      ToastUtils.error(LocalizationUtils.localizeError())
      return
    }

    dispatch(elearninSessionReady(result.startElearning.elearningSessionId))
    const elearningScormDataBuilder = new ElearningScormDataBuilder(
      result?.startElearning.scormData,
      elearningDetails.minimumScore
    )

    const scormRuntimeData = elearningScormDataBuilder.build()
    const { height, width, isResponsive, canBeResized, canBeMaximized }: ScormDisplayData =
      elearningDetails?.scormData

    const scormRunnerPayload = {
      scormRuntimeData,
      scormDisplayData: {
        height,
        width,
        isResponsive,
        canBeResized,
        canBeMaximized,
      },
    }
    dispatch(scormDataCreated(scormRunnerPayload))
  }

const startSession = (chapterId: number, resultId: Guid) => {
  return createApiAction({
    apiCalls: {
      startElearning: () => startElearningSession(chapterId, resultId),
    },
    payloadAction: showSpinner,
    nextAction: handleScormData,
  })
}

export const commitChapterProgress =
  (
    data: WritableScormRuntimeData,
    resultId?: Guid,
    finish?: boolean,
    isProcessing = true
  ): AppThunk =>
  async (dispatch, getState) => {
    const {
      runningChapterId,
      activeElearningSessionId,
      runningChapterScromData,
      finishScheduled,
      activeResultId,
    } = getState().elearningDetails

    if (
      (!resultId && !activeResultId) ||
      !runningChapterId ||
      !activeElearningSessionId ||
      (finishScheduled && !finish)
    ) {
      return
    }

    let totalTime = data.TotalTime
    if (finish) {
      const prevTotalTimeInSeconds = convertFromScormTimeToSeconds(totalTime)
      const sessionTimeInSecondns = data.SessionTime
        ? convertFromScormTimeToSeconds(data.SessionTime)
        : 0
      const totalSeconds = prevTotalTimeInSeconds + sessionTimeInSecondns
      totalTime = convertToScormTimeSpan(totalSeconds)
    }

    const progressUpdateDto: ElearningProgressUpdateDto = {
      scormData: {
        lessonLocation: data.LessonLocation,
        lessonStatus: CoreLessonStatus[data.LessonStatus],
        scoreRaw: +data.ScoreRaw || 0,
        exit: CoreExit[data.Exit],
        sessionTime: data.SessionTime,
        suspendData: data.SuspendData,
        totalTime: totalTime,
        launchData: runningChapterScromData?.scormRuntimeData.LaunchData || '',
      },
      resultId: activeResultId || resultId || '',
      chapterId: runningChapterId,
      elearningSessionId: activeElearningSessionId,
      isFinish: !!finish,
    }
    const updateAction = createApiAction({
      apiCalls: {
        updateElearningProgress: () => updateElearningProgress(progressUpdateDto),
      },
      payloadAction: finish ? noop : increaseCommitsInProgress,
      nextAction: finish ? closeModule : checkScheduledFinish,
      shouldCallApi: () => !!isProcessing,
    })

    return dispatch(updateAction)
  }

export const checkScheduledFinish = (): AppThunk => async (dispatch, getState) => {
  dispatch(decreaseCommitsInProgress())
  const { commitsInProgress, finishScheduled, scheduledFinishData, elearningDetailsData } =
    getState().elearningDetails

  if (commitsInProgress === 0 && finishScheduled && scheduledFinishData) {
    dispatch(
      commitChapterProgress(
        scheduledFinishData,
        elearningDetailsData.result?.elearningDetails.result?.id,
        true
      )
    )
  }
}

export const scheduleFinishChapter =
  (data: WritableScormRuntimeData, resultId?: Guid): AppThunk =>
  async (dispatch, getState) => {
    const { commitsInProgress, finishScheduled } = getState().elearningDetails
    if (finishScheduled) {
      // no other action can be triggered after finish has been scheduled
      return
    }

    dispatch(setFinishScheduled(true))
    if (commitsInProgress === 0) {
      dispatch(commitChapterProgress(data, resultId, true))
    } else {
      dispatch(setScheduledFinishData(data))
    }
  }

export const deleteResult = (resultId: Guid) => {
  return createApiAction({
    apiCalls: {
      deleteResult: () => deleteElearningResult(resultId),
    },
    payloadAction: showSpinner,
    nextAction: resetElearningDetails,
  })
}

export const finalizeResult = (resultId: Guid) => {
  return createApiAction({
    apiCalls: {
      finalizeResult: () => finalizeElearningResult(resultId),
    },
    payloadAction: showSpinner,
    nextAction: handleSubmitResult,
  })
}

export const showNoChapterToRun = (): AppThunk => () => {
  ToastUtils.warning(OceanLocalizationUtils.localize('warnings.ElearningAllChaptersCompleted'))
}

export const loadFeedbackFormData = () => {
  return createApiAction({
    apiCalls: {
      elearningFeedbackForm: () => getFeedbackForm(),
    },
    payloadAction: feedbackFormLoaded,
    enableErrorNotification: true,
  })
}

export const saveElearningFeedback = (
  resultId: Guid,
  answers: FeedbackAnswer[],
  freeTextAnswers: FeedbackFreeTextAnswer[]
) => {
  return createApiAction({
    apiCalls: {
      saveFeedback: () => saveFeedback(resultId, answers, freeTextAnswers),
    },
    payloadAction: elearningFeedbackSaved,
  })
}

const closeModule =
  (response: ApiMiddlewarePayload<void>): AppThunk =>
  async (dispatch, getState) => {
    const elearningDetailsState = getState().elearningDetails
    const elearningDetailsData = elearningDetailsState.elearningDetailsData.result?.elearningDetails
    const moduleId = elearningDetailsData?.moduleId
    if (!moduleId) return

    const notExistingSessionErrorCode = 'UpdateElearningProgressSessionDoesNotExistResponse'
    if (response.error?.code === notExistingSessionErrorCode) {
      ToastUtils.error(LocalizationUtils.localizeError(notExistingSessionErrorCode))
    }

    dispatch(closeChapter())
    dispatch(loadElearningDetailsData(moduleId) as MiddlewareApiAction)
  }

const handleSubmitResult =
  (response: ApiMiddlewarePayload<void>): AppThunk =>
  async (dispatch, getState) => {
    dispatch(hideSpinner())

    const { error, result } = response
    if (error || !result) {
      const code = `${error?.code}${error?.details ? error.details.requirementsNotMetMessage : ''}`
      ToastUtils.error(LocalizationUtils.localizeError(code, error?.details))
      return
    }

    const showFeedbackForm =
      getState().elearningDetails.elearningDetailsData.result?.elearningDetails.showFeedbackForm
    dispatch(resetElearningDetails())

    if (showFeedbackForm) {
      dispatch(setFeedbackVisible(true))
    }
  }

const resetElearningDetails = (): AppThunk => async (dispatch, getState) => {
  dispatch(hideSpinner())
  const moduleId =
    getState().elearningDetails.elearningDetailsData.result?.elearningDetails.moduleId
  if (!moduleId) return
  dispatch(loadElearningDetailsData(moduleId) as MiddlewareApiAction)
}

export const handleRetractedModuleCommand =
  (command: ElearningRetractionCommand): AppThunk =>
  dispatch => {
    switch (command) {
      case ElearningRetractionCommand.Cancel:
      case ElearningRetractionCommand.Ok:
        dispatch(goBackAfterRetracted())
        break
      case ElearningRetractionCommand.ProceedToTraining:
        dispatch(resumeTraining())
        break
      case ElearningRetractionCommand.StartOver:
        dispatch(startOverAfterRetracted())
        break
      case ElearningRetractionCommand.OkRemoveResult:
        dispatch(deleteAfterRetraction(false))
        break
      case ElearningRetractionCommand.No:
      case ElearningRetractionCommand.DeleteResultAndStartNewSession:
        dispatch(startOverAfterRetracted())
        break
      case ElearningRetractionCommand.SaveAndClose:
        dispatch(saveAfterRetraction(false))
        break
      case ElearningRetractionCommand.SaveAndStartNew:
        dispatch(saveAfterRetraction())
        break
      default:
        break
    }
  }

const deleteAfterRetraction =
  (reload: boolean = true): AppThunk =>
  async (dispatch, getState) => {
    const resultId =
      getState().elearningDetails.elearningDetailsData.result?.elearningDetails.result?.id
    if (!resultId) return

    const response: ApiResponse<void> = await deleteElearningResult(resultId)
    const { error } = response
    if (error) {
      ToastUtils.error(LocalizationUtils.localizeError(error?.code))
      return
    }
    if (reload) {
      dispatch(resetElearningDetails())
    } else {
      ReferrerUtils.dispatchGoBack(dispatch)
    }
  }

const goBackAfterRetracted = (): AppThunk => async dispatch => {
  ReferrerUtils.dispatchGoBack(dispatch)
}

const startOverAfterRetracted =
  (force: boolean = true): AppThunk =>
  async (dispatch, getState) => {
    const elearningDetailsState = getState().elearningDetails
    const elearningDetailsResponse =
      elearningDetailsState.elearningDetailsData.result?.elearningDetails
    if (!elearningDetailsState || !elearningDetailsResponse) return

    const moduleId = elearningDetailsResponse?.moduleId
    const activeLanguage = elearningDetailsState.activeLanguage

    const response: ApiResponse<void> = await createElearningResult(moduleId, activeLanguage, force)
    const { error } = response
    if (error) {
      ToastUtils.error(LocalizationUtils.localizeError(error?.code))
      return
    }
    dispatch(resetElearningDetails())
  }

const saveAfterRetraction =
  (reload: boolean = true): AppThunk =>
  async (dispatch, getState) => {
    const resultId =
      getState().elearningDetails.elearningDetailsData.result?.elearningDetails.result?.id
    if (!resultId) return

    const response: ApiResponse<void> = await finalizeElearningResult(resultId)
    const { error } = response
    if (error) {
      ToastUtils.error(LocalizationUtils.localizeError(error?.code))
      return
    }

    if (reload) {
      dispatch(resetElearningDetails())
    } else {
      ReferrerUtils.dispatchGoBack(dispatch)
    }
  }
